
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ContactForm from '../components/ContactForm';

const boardMembers = [
  {
    name: "Matthew Kless",
    role: "Founder & Executive Director",
    image: "https://storage.googleapis.com/hostinger-horizons-assets-prod/820f4759-a4d1-4dcc-a468-10bb199b3f40/46041e126e5009ec7ecd72f65f21245b.jpg",
    expertise: "Ski Instruction & Environmental Advocacy",
    bio: "From student to instructor at Mohawk Mountain, Matthew's journey in winter sports has come full circle. Starting in the programs at Mohawk as a kid, he completed their specialized instructor training in 2022/2023. In 2023, he began his first season as a fully fledged instructor, teaching his own students and taking on new responsibilities. His documentary 'Frozen Gold' exploring climate change's impact on skiing inspired the creation of The Powder Project.",
    achievements: [
      "Certified Ski Instructor at Mohawk Mountain",
      "Creator of 'Frozen Gold' Documentary",
      "Founded The Powder Project",
      "Youth Program Development"
    ],
    quote: "Whether it's helping beginners find their balance or pushing advanced skiers to refine their techniques, I love seeing my students gain confidence. Teaching also sharpened my leadership and communication skills. Getting people good at hurtling down a mountain is not easy, but it sure is rewarding."
  },
  {
    name: "Kelsey Knopp",
    role: "Board Member",
    image: "https://storage.googleapis.com/hostinger-horizons-assets-prod/820f4759-a4d1-4dcc-a468-10bb199b3f40/e3f04007c9c231a54f9bce95602b857a.jpg",
    expertise: "Professional Skiing & Mountain Operations",
    bio: "A passionate skier and instructor at Altitude Ski and Snowboard School, Kelsey brings extensive experience in winter sports education and mountain operations. Her dedication to preserving winter sports and teaching the next generation aligns perfectly with The Powder Project's mission.",
    achievements: [
      "Professional Ski Instructor",
      "Mountain Operations Expert",
      "Youth Program Development",
      "Environmental Education Advocate"
    ],
    quote: "Teaching skiing isn't just about technique - it's about sharing the love for winter sports and ensuring these experiences are preserved for future generations."
  },
  {
    name: "Brendan Conry",
    role: "Board Member",
    image: "https://storage.googleapis.com/hostinger-horizons-assets-prod/820f4759-a4d1-4dcc-a468-10bb199b3f40/99414e6e03a3cc54ffa599c19cad3f17.jpg",
    expertise: "Business Development & Strategy",
    bio: "With a strong background in business development and strategic planning, Brendan brings valuable expertise to The Powder Project. His passion for winter sports combined with his business acumen helps drive the organization's growth and impact.",
    achievements: [
      "Strategic Planning Expert",
      "Business Development Professional",
      "Environmental Initiative Leader",
      "Community Engagement Specialist"
    ],
    quote: "Combining business expertise with environmental advocacy creates powerful opportunities for meaningful change in winter sports conservation."
  }
];

const AboutPage = () => {
  const [selectedMember, setSelectedMember] = useState(null);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-900 to-black py-24">
      <div className="max-w-6xl mx-auto px-4">
        {/* Board Members Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {boardMembers.map((member, index) => (
            <motion.div
              key={member.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.2 }}
              className="relative"
              onClick={() => setSelectedMember(selectedMember === index ? null : index)}
            >
              <div className="relative aspect-square rounded-xl overflow-hidden cursor-pointer">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/50 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <h3 className="text-2xl font-bold text-white mb-2">{member.name}</h3>
                  <p className="text-lg text-blue-200">{member.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Full-width Expanded Content */}
        <AnimatePresence>
          {selectedMember !== null && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="mt-8 bg-white/10 backdrop-blur-sm rounded-xl p-8 col-span-full"
            >
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h2 className="text-3xl font-bold text-white mb-6">{boardMembers[selectedMember].name}</h2>
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-white mb-3">Expertise</h3>
                    <p className="text-blue-200">{boardMembers[selectedMember].expertise}</p>
                  </div>
                  <div className="mb-6">
                    <p className="text-blue-200 leading-relaxed">{boardMembers[selectedMember].bio}</p>
                  </div>
                </div>
                
                <div>
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-white mb-4">Achievements</h3>
                    <ul className="space-y-2">
                      {boardMembers[selectedMember].achievements.map((achievement, i) => (
                        <li key={i} className="flex items-center text-blue-200">
                          <span className="text-green-400 mr-2">✓</span>
                          {achievement}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="italic text-blue-200 text-lg">
                    "{boardMembers[selectedMember].quote}"
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto mt-24"
        >
          <h2 className="text-3xl font-bold text-white text-center mb-8">Contact Us</h2>
          <ContactForm />
        </motion.div>
      </div>
    </div>
  );
};

export default AboutPage;
